import apiFetch from "@wordpress/api-fetch";
import { __ } from "@wordpress/i18n";
import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import PageHeading from "../../components/PageHeading/PageHeading";
import useAdminMenuHighlight from "../../hooks/useAdminMenuHighlight";
import "./Logs.scss";

const Logs = () => {
	useAdminMenuHighlight();
	const [searchQuery, setSearchQuery] = useState("");
	const [logs, setLogs] = useState([]);
	const [filteredLogs, setFilteredLogs] = useState([]);
	const [logActiveFilter, setLogActiveFilter] = useState("all");
	const logFilterList = [
		{ name: "All", value: "all" },
		{ name: "Today", value: "today" },
		{ name: "Last 7 Days", value: "week" },
		{ name: "This Month", value: "month" },
	];

	const parseDate = (dateStr) => {
		return new Date(dateStr.replace(" ", "T")); // "2025-08-21T07:56:05"
	};
	const logHandleDatatableFilter = (range) => {
		setLogActiveFilter(range);

		if (range === "all") {
			setFilteredLogs(logs);
			return;
		}

		const now = new Date();
		const todayStart = new Date(
			now.getFullYear(),
			now.getMonth(),
			now.getDate(),
		);
		const weekStart = new Date(todayStart);
		weekStart.setDate(weekStart.getDate() - weekStart.getDay()); // start of week (Sunday)
		const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

		const newData = logs.filter((row) => {
			const rowDate = parseDate(row.created_at);

			if (range === "today") {
				return rowDate >= todayStart;
			}
			if (range === "week") {
				// return rowDate >= weekStart;
				const sevenDaysAgo = new Date(now);
				sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
				return rowDate >= sevenDaysAgo;
			}
			if (range === "month") {
				return rowDate >= monthStart;
			}
			return true;
		});

		setFilteredLogs(newData);
	};

	function timeAgo(inputDate) {
		// Parse the input date as UTC
		const pastDate = new Date(inputDate + " UTC");
		const now = new Date();

		const nowUTC = Date.UTC(
			now.getUTCFullYear(),
			now.getUTCMonth(),
			now.getUTCDate(),
			now.getUTCHours(),
			now.getUTCMinutes(),
			now.getUTCSeconds(),
		);
		const pastDateUTC = Date.UTC(
			pastDate.getUTCFullYear(),
			pastDate.getUTCMonth(),
			pastDate.getUTCDate(),
			pastDate.getUTCHours(),
			pastDate.getUTCMinutes(),
			pastDate.getUTCSeconds(),
		);

		const diffInSeconds = Math.floor((nowUTC - pastDateUTC) / 1000);

		const units = [
			{ label: "year", seconds: 60 * 60 * 24 * 365 },
			{ label: "month", seconds: 60 * 60 * 24 * 30 },
			{ label: "week", seconds: 60 * 60 * 24 * 7 },
			{ label: "day", seconds: 60 * 60 * 24 },
			{ label: "hour", seconds: 60 * 60 },
			{ label: "minute", seconds: 60 },
			{ label: "second", seconds: 1 },
		];

		for (const unit of units) {
			const count = Math.floor(diffInSeconds / unit.seconds);
			if (count > 0) {
				return `${count} ${unit.label}${count > 1 ? "s" : ""} ago`;
			}
		}

		return "Just now";
	}

	const columns = [
		{
			id: "id",
			name: __("ID", "ultimate-security"),
			cell: (row) => (
				<div className="d-flex flex-wrap align-items-center gap-2">
					<span>
						<b>{row.event_id ? row.event_id : "N/A"}</b>
					</span>
				</div>
			),
		},
		{
			id: "severity",
			name: __("Severity", "ultimate-security"),
			cell: (row) => (
				<div className="d-flex flex-wrap align-items-center gap-2">
					<span>
						<b>{row.severity ? row.severity : "N/A"}</b>
					</span>
				</div>
			),
		},
		{
			id: "date",
			name: __("Date", "ultimate-security"),
			cell: (row) => {
				if (!row.created_at) {
					return <span>{__("N/A", "ultimate-security")}</span>;
				}

				const date = new Date(row.created_at);

				// Format date → August 21, 2025
				const formattedDate = date.toLocaleDateString("en-US", {
					year: "numeric",
					month: "long",
					day: "2-digit",
				});

				// Get raw time parts
				const hours = date.getHours() % 12 || 12; // convert to 12-hour format
				const minutes = String(date.getMinutes()).padStart(2, "0");
				const seconds = String(date.getSeconds()).padStart(2, "0");
				const millis = String(date.getMilliseconds()).padStart(3, "0");
				const ampm = date.getHours() >= 12 ? "pm" : "am";

				const formattedTime = `${hours}:${minutes}:${seconds}.${millis} ${ampm}`;

				return (
					<div className="d-flex flex-column">
						<span className="time-ago">
							{timeAgo(row.created_at)}
						</span>
						<span>
							<b>{formattedDate}</b>
						</span>
						<small>{formattedTime}</small>
					</div>
				);
			},
		},

		{
			id: "user",
			name: __("User", "ultimate-security"),
			cell: (row) => (
				<div className="d-flex align-items-center gap-2">
					<img
						src={
							row.user_avatar
								? row.user_avatar
								: ultimate_security_ajax_obj.imageUrl +
								  "no-avatar.svg"
						}
						alt="avatar"
						width={"20"}
						height={"20"}
						className="flex-shrink-0"
					/>
					<div className="d-flex flex-column">
						<span className="fw-bold mb-0">
							{row.display_name ? row.display_name : "N/A"}
						</span>
						<small className="text-muted mb-0">
							{row.user_role ? row.user_role : "N/A"}
						</small>
					</div>
				</div>
			),
		},

		{
			id: "ip",
			name: __("IP", "ultimate-security"),
			cell: (row) => (
				<div className="d-flex flex-wrap align-items-center gap-2">
					<span>
						<b>{row.user_ip ? row.user_ip : "N/A"}</b>
					</span>
				</div>
			),
		},
		{
			id: "agent",
			name: __("Agent", "ultimate-security"),
			cell: (row) => (
				<div className="d-flex flex-wrap align-items-center gap-2">
					<span>
						<b>{row.user_agent ? row.user_agent : "N/A"}</b>
					</span>
				</div>
			),
		},
		{
			id: "object",
			name: __("Object", "ultimate-security"),
			cell: (row) => (
				<div className="d-flex flex-wrap align-items-center gap-2">
					<span>
						<b>{row.object ? row.object : "N/A"}</b>
					</span>
				</div>
			),
		},
		{
			id: "event_type",
			name: __("Event Type", "ultimate-security"),
			cell: (row) => (
				<div className="d-flex flex-wrap align-items-center gap-2">
					<span>
						<b>{row.type ? row.type : "N/A"}</b>
					</span>
				</div>
			),
		},

		{
			id: "login-activity",
			name: __("Details", "ultimate-security"),
			cell: (row) => (
				<div className="login-activity-wrap">
					<span className="formated-time">{row.message}</span>
				</div>
			),
			ignoreRowClick: true,
			allowoverflow: true,
			minwidth: "250px",
			style: {
				display: "flex",
				justifyContent: "start",
				paddingRight: "11px",
			},
		},
	];

	useEffect(() => {
		fetchHistories("all");
	}, []);

	// Filter logs whenever logs or searchQuery changes
	useEffect(() => {
		if (searchQuery.trim() === "") {
			setFilteredLogs(logs);
		} else {
			const newData = logs.filter((row) => {
				return (
					(row.display_name &&
						row.display_name
							.toLowerCase()
							.includes(searchQuery.toLowerCase())) ||
					(row.message &&
						row.message
							.toLowerCase()
							.includes(searchQuery.toLowerCase())) ||
					(row.type &&
						row.type
							.toLowerCase()
							.includes(searchQuery.toLowerCase())) ||
					(row.user_ip &&
						row.user_ip
							.toLowerCase()
							.includes(searchQuery.toLowerCase())) ||
					(row.object &&
						row.object
							.toLowerCase()
							.includes(searchQuery.toLowerCase())) ||
					(row.user_role &&
						row.user_role
							.toLowerCase()
							.includes(searchQuery.toLowerCase())) ||
					(row.severity &&
						row.severity
							.toLowerCase()
							.includes(searchQuery.toLowerCase())) ||
					(row.created_at &&
						row.created_at
							.toLowerCase()
							.includes(searchQuery.toLowerCase())) ||
					(row.event_id &&
						row.event_id
							.toLowerCase()
							.includes(searchQuery.toLowerCase())) ||
					(row.user_agent &&
						row.user_agent
							.toLowerCase()
							.includes(searchQuery.toLowerCase()))
				);
			});
			setFilteredLogs(newData);
		}
	}, [logs, searchQuery]);

	const fetchHistories = async (limit = null, offset = 0) => {
		try {
			let path = `/ultimate-security/v1/get-histories`;
			const params = [];
			if (limit !== null) params.push(`limit=${limit}`);
			if (offset > 0) params.push(`offset=${offset}`);
			if (params.length) path += `?${params.join("&")}`;

			const result = await apiFetch({ path });
			setLogs(result);
			setLogs(result);
			setFilteredLogs(result);
		} catch (err) {
			console.error("Error fetching histories:", err);
		}
	};

	const handleSelectedRowsChange = ({ selectedRows }) => {
		setSelectedRows(selectedRows);
	};

	const handleUserSearch = (e) => {
		const query = e.target.value;
		setSearchQuery(query);
	};

	return (
		<>
			<div className="ultimate-security-logs-wrapper">
				<div className="container-fluid">
					<div className="form-header pb-lg mb-lg border-bottom">
						<PageHeading
							title={__("Logs", "ultimate-security")}
							subtitle={__(
								"View and manage security logs for your website.",
								"ultimate-security",
							)}
						/>
					</div>
					<div className="setting-unit">
						<div className="user-datatable datatable-unit">
							<div className="row align-items-center mb-30">
								<div className="col-7 datatable-filters">
									<div className="d-flex flex-wrap">
										{logFilterList &&
											logFilterList.map((item) => (
												<span
													key={item.value}
													onClick={() =>
														logHandleDatatableFilter(
															item.value,
														)
													}
													className={
														logActiveFilter ===
														item.value
															? "active"
															: ""
													}
												>
													{item.name}
												</span>
											))}
									</div>
								</div>
								<div className="col-5">
									<div className="search-unit d-flex gap-2 justify-content-end">
										<input
											placeholder="Search"
											type="search"
											onChange={handleUserSearch}
											value={searchQuery}
										/>
									</div>
								</div>
							</div>
							<div className="table-wrapper">
								<DataTable
									columns={columns}
									data={filteredLogs} // Use filtered data
									selectableRows
									onSelectedRowsChange={
										handleSelectedRowsChange
									}
									pagination
									paginationPerPage={15} // Default to 5 rows per page
									paginationRowsPerPageOptions={[15, 25, 50]} // Options with 5 first
									dense
									responsive
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};
export default Logs;
